import React, { useEffect, useRef, useState } from "react";

/**
 * Counter — clear hierarchy.
 *
 *     0 / 1,000,000
 *   MESSAGES SWALLOWED
 *
 * The number dominates. Denominator is subordinate. Label is small and spaced.
 * Milestone atmosphere lines still appear near thresholds.
 */
export default function Counter({ count, target }) {
  const [pulse, setPulse] = useState(false);
  const prev = useRef(count);

  useEffect(() => {
    if (count !== prev.current) {
      setPulse(true);
      const t = setTimeout(() => setPulse(false), 950);
      prev.current = count;
      return () => clearTimeout(t);
    }
  }, [count]);

  const nf = new Intl.NumberFormat("en-US");

  // Milestone atmosphere — never explains the event, only hints at proximity.
  let atmosphere = "";
  if (count >= 999999) atmosphere = "One remains.";
  else if (count >= 999990) atmosphere = "Ten remain.";
  else if (count >= 999000) atmosphere = "We are almost there.";
  else if (count >= 900000) atmosphere = "Something is getting closer.";
  else if (count >= 500000) atmosphere = "Halfway into the dark.";

  return (
    <div className="text-center select-none" data-testid="void-counter">
      <div
        className={`font-mono-void font-light drift-in ${pulse ? "pulse-glow" : ""}`}
        style={{
          lineHeight: 0.95,
          letterSpacing: "0.02em",
          color: "#fff",
          textShadow: "0 0 60px rgba(0,0,0,0.85), 0 0 40px rgba(255,220,180,0.06)",
        }}
        aria-live="polite"
      >
        <span
          data-testid="void-counter-number"
          style={{ fontSize: "clamp(72px, 12vw, 156px)" }}
        >
          {nf.format(count)}
        </span>
        <span
          data-testid="void-counter-target"
          style={{
            fontSize: "clamp(20px, 2.4vw, 32px)",
            color: "rgba(255,255,255,0.35)",
            marginLeft: "0.35em",
            letterSpacing: "0.04em",
          }}
        >
          / {nf.format(target)}
        </span>
      </div>

      <div
        className="mt-6 font-ui text-white/55 drift-in drift-delay-1"
        style={{
          fontSize: "10px",
          letterSpacing: "0.5em",
          textShadow:
            "0 0 22px rgba(0,0,0,1), 0 0 8px rgba(0,0,0,1), 0 0 4px rgba(0,0,0,1)",
        }}
      >
        Messages Swallowed
      </div>

      {atmosphere && (
        <div
          className="mt-10 font-serif text-white/55"
          style={{
            fontSize: "clamp(15px, 1.6vw, 19px)",
            fontVariationSettings: '"opsz" 60, "SOFT" 30',
          }}
          data-testid="void-atmosphere"
        >
          {atmosphere}
        </div>
      )}
    </div>
  );
}
